import { Suspense, lazy, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { AuthProvider, useAuth } from '@/features/auth/AuthContext';
import { homePathFor } from '@/nav/navigation';
import { SignInPage } from '@/features/auth/SignInPage';
import { ForgotPasswordPage } from '@/features/auth/ForgotPasswordPage';
import { ResetPasswordPage } from '@/features/auth/ResetPasswordPage';
import { AcceptInvitationPage } from '@/features/auth/AcceptInvitationPage';
import { PlaceholderPage } from '@/features/placeholder/PlaceholderPage';
import { ToastProvider } from '@/components/ui/ToastProvider';
import { useProjects } from '@/features/projects/queries';
import { CurrentProjectProvider, useCurrentProject } from '@/features/projects/CurrentProjectContext';
import { NAV_ITEMS, SUB_PAGE_LABELS } from '@/nav/navigation';
import type { ProjectOption } from '@/components/layout/ProjectSelector';
import { ConstructionLoader } from '@/components/ui/ConstructionLoader';

/**
 * Feature screens are split per route.
 *
 * Leaflet and the QR encoder are ~180kB between them and are needed on two
 * screens only. Shipping them to someone opening the staff list on site 3G is
 * the difference between a usable app and a blank one, so every module below is
 * fetched on demand. Auth screens stay eager — they are the entry point.
 */
const WeeklyAttendancePage = lazy(() =>
  import('@/features/sitemanagement/WeeklyAttendancePage').then((m) => ({ default: m.WeeklyAttendancePage })),
);
const DirectoryPage = lazy(() =>
  import('@/features/directory/DirectoryPage').then((m) => ({ default: m.DirectoryPage })),
);
const DirectoryDetailPage = lazy(() =>
  import('@/features/directory/DirectoryDetailPage').then((m) => ({ default: m.DirectoryDetailPage })),
);
const DirectoryFormPage = lazy(() =>
  import('@/features/directory/DirectoryFormPage').then((m) => ({ default: m.DirectoryFormPage })),
);
const SiteDiariesPage = lazy(() =>
  import('@/features/sitediary/SiteDiariesPage').then((m) => ({ default: m.SiteDiariesPage })),
);
const DefectsPage = lazy(() =>
  import('@/features/defects/DefectsPage').then((m) => ({ default: m.DefectsPage })),
);
const DefectDetailPage = lazy(() =>
  import('@/features/defects/DefectDetailPage').then((m) => ({ default: m.DefectDetailPage })),
);
const CompliancePage = lazy(() =>
  import('@/features/compliance/CompliancePage').then((m) => ({ default: m.CompliancePage })),
);
const ComplianceDocumentPage = lazy(() =>
  import('@/features/compliance/ComplianceDocumentPage').then((m) => ({ default: m.ComplianceDocumentPage })),
);
const NotificationsPage = lazy(() =>
  import('@/features/notifications/NotificationsPage').then((m) => ({ default: m.NotificationsPage })),
);
const AgentPage = lazy(() => import('@/features/agent/AgentPage').then((m) => ({ default: m.AgentPage })));
const DashboardsPage = lazy(() =>
  import('@/features/dashboards/DashboardsPage').then((m) => ({ default: m.DashboardsPage })),
);
const KpiScorecardsPage = lazy(() =>
  import('@/features/company/KpiScorecardsPage').then((m) => ({ default: m.KpiScorecardsPage })),
);
const OrgChartPage = lazy(() =>
  import('@/features/company/OrgChartPage').then((m) => ({ default: m.OrgChartPage })),
);
const DataImportPage = lazy(() =>
  import('@/features/company/DataImportPage').then((m) => ({ default: m.DataImportPage })),
);
const TemplatesPage = lazy(() =>
  import('@/features/company/TemplatesPage').then((m) => ({ default: m.TemplatesPage })),
);
/*
 * The document editor and viewer. Lazy on its own, because TipTap and the Word
 * converter behind it are the heaviest thing in the bundle and nobody on the
 * QR gate ever opens a policy.
 */
const DocumentPage = lazy(() =>
  import('@/features/company/documents/DocumentPage').then((m) => ({ default: m.DocumentPage })),
);
const ChecklistMasterPage = lazy(() =>
  import('@/features/company/ChecklistMasterPage').then((m) => ({ default: m.ChecklistMasterPage })),
);
const ProjectDocumentPage = lazy(() =>
  import('@/features/company/documents/DocumentPage').then((m) => ({ default: m.ProjectDocumentPage })),
);
const SopsPage = lazy(() =>
  import('@/features/company/SopsPage').then((m) => ({ default: m.SopsPage })),
);
const PeoplePage = lazy(() =>
  import('@/features/people/PeoplePage').then((m) => ({ default: m.PeoplePage })),
);
const PeopleDashboardPage = lazy(() =>
  import('@/features/people/PeopleDashboardPage').then((m) => ({ default: m.PeopleDashboardPage })),
);
const PersonFormPage = lazy(() =>
  import('@/features/people/PersonFormPage').then((m) => ({ default: m.PersonFormPage })),
);
const PersonDetailPage = lazy(() =>
  import('@/features/people/PersonDetailPage').then((m) => ({ default: m.PersonDetailPage })),
);
const ScorecardFormPage = lazy(() =>
  import('@/features/scorecards/ScorecardFormPage').then((m) => ({ default: m.ScorecardFormPage })),
);
const ScorecardsPage = lazy(() =>
  import('@/features/scorecards/ScorecardsPage').then((m) => ({ default: m.ScorecardsPage })),
);
const SkillCataloguePage = lazy(() =>
  import('@/features/skills/SkillCataloguePage').then((m) => ({ default: m.SkillCataloguePage })),
);
const SkillsMatrixPage = lazy(() =>
  import('@/features/skills/SkillsMatrixPage').then((m) => ({ default: m.SkillsMatrixPage })),
);
const OnboardingPage = lazy(() =>
  import('@/features/onboarding/OnboardingPage').then((m) => ({ default: m.OnboardingPage })),
);
const QuickHoursPage = lazy(() =>
  import('@/features/timesheets/QuickHoursPage').then((m) => ({ default: m.QuickHoursPage })),
);
const SiteManagementPage = lazy(() =>
  import('@/features/sitemanagement/SiteManagementPage').then((m) => ({ default: m.SiteManagementPage })),
);
const ProjectsPage = lazy(() =>
  import('@/features/projects/ProjectsPage').then((m) => ({ default: m.ProjectsPage })),
);
const ProjectFormPage = lazy(() =>
  import('@/features/projects/ProjectFormPage').then((m) => ({ default: m.ProjectFormPage })),
);
const ProjectDetailPage = lazy(() =>
  import('@/features/projects/ProjectDetailPage').then((m) => ({ default: m.ProjectDetailPage })),
);
const ToolFormPage = lazy(() =>
  import('@/features/warehouse/ToolFormPage').then((m) => ({ default: m.ToolFormPage })),
);
const InventoryFormPage = lazy(() =>
  import('@/features/warehouse/InventoryFormPage').then((m) => ({ default: m.InventoryFormPage })),
);
const WarehousePage = lazy(() =>
  import('@/features/warehouse/WarehousePage').then((m) => ({ default: m.WarehousePage })),
);
const ProspectFormPage = lazy(() =>
  import('@/features/prospects/ProspectFormPage').then((m) => ({ default: m.ProspectFormPage })),
);
const ProspectsPage = lazy(() =>
  import('@/features/prospects/ProspectsPage').then((m) => ({ default: m.ProspectsPage })),
);
const PreConstructionPage = lazy(() =>
  import('@/features/preconstruction/PreConstructionPage').then((m) => ({ default: m.PreConstructionPage })),
);
const TenderFormPage = lazy(() =>
  import('@/features/preconstruction/TenderFormPage').then((m) => ({ default: m.TenderFormPage })),
);
const TenderProfilePage = lazy(() =>
  import('@/features/preconstruction/TenderProfilePage').then((m) => ({ default: m.TenderProfilePage })),
);
const SafetyPage = lazy(() =>
  import('@/features/safety/SafetyPage').then((m) => ({ default: m.SafetyPage })),
);
const CertificationTypeFormPage = lazy(() =>
  import('@/features/safety/CertificationTypeFormPage').then((m) => ({
    default: m.CertificationTypeFormPage,
  })),
);
const InductionCourseFormPage = lazy(() =>
  import('@/features/safety/InductionCourseFormPage').then((m) => ({
    default: m.InductionCourseFormPage,
  })),
);
const InductionBuilderPage = lazy(() =>
  import('@/features/safety/InductionBuilderPage').then((m) => ({ default: m.InductionBuilderPage })),
);
const RollCallPage = lazy(() =>
  import('@/features/evacuation/RollCallPage').then((m) => ({ default: m.RollCallPage })),
);
const CompanyStatementPage = lazy(() =>
  import('@/features/company/CompanyStatementPage').then((m) => ({ default: m.CompanyStatementPage })),
);
const ArchivePage = lazy(() =>
  import('@/features/archive/ArchivePage').then((m) => ({ default: m.ArchivePage })),
);
const LessonsPage = lazy(() =>
  import('@/features/lessons/LessonsPage').then((m) => ({ default: m.LessonsPage })),
);
const TriggersPage = lazy(() =>
  import('@/features/company/TriggersPage').then((m) => ({ default: m.TriggersPage })),
);
const WipSchedulePage = lazy(() =>
  import('@/features/wip/WipSchedulePage').then((m) => ({ default: m.WipSchedulePage })),
);
const RolesPage = lazy(() =>
  import('@/features/roles/RolesPage').then((m) => ({ default: m.RolesPage })),
);
const CompanyPage = lazy(() =>
  import('@/features/company/CompanyPage').then((m) => ({ default: m.CompanyPage })),
);
const InsightsPage = lazy(() =>
  import('@/features/insights/InsightsPage').then((m) => ({ default: m.InsightsPage })),
);
const MySiteRecordPage = lazy(() =>
  import('@/features/me/MySiteRecordPage').then((m) => ({ default: m.MySiteRecordPage })),
);
const ProfilePage = lazy(() =>
  import('@/features/profile/ProfilePage').then((m) => ({ default: m.ProfilePage })),
);
const DevelopmentPage = lazy(() =>
  import('@/features/development/DevelopmentPage').then((m) => ({ default: m.DevelopmentPage })),
);
const TicketFormPage = lazy(() =>
  import('@/features/development/TicketFormPage').then((m) => ({ default: m.TicketFormPage })),
);
const TicketDetailPage = lazy(() =>
  import('@/features/development/TicketDetailPage').then((m) => ({ default: m.TicketDetailPage })),
);
const ProgrammePage = lazy(() => import('@/features/programme/ProgrammePage').then((m) => ({ default: m.ProgrammePage })));
const PortfolioProgrammePage = lazy(() =>
  import('@/features/programme/PortfolioProgrammePage').then((m) => ({ default: m.PortfolioProgrammePage })),
);
const FinancePage = lazy(() => import('@/features/finance/FinancePage').then((m) => ({ default: m.FinancePage })));
const BudgetPage = lazy(() => import('@/features/finance/BudgetPage').then((m) => ({ default: m.BudgetPage })));
const PurchaseOrderPage = lazy(() =>
  import('@/features/finance/PurchaseOrderPage').then((m) => ({ default: m.PurchaseOrderPage })),
);
const PurchaseOrderFormPage = lazy(() =>
  import('@/features/finance/PurchaseOrderFormPage').then((m) => ({ default: m.PurchaseOrderFormPage })),
);
const ProjectFinancePage = lazy(() =>
  import('@/features/finance/ProjectFinancePage').then((m) => ({ default: m.ProjectFinancePage })),
);
const SiteSignOnPage = lazy(() =>
  import('@/features/signon-public/SiteSignOnPage').then((m) => ({ default: m.SiteSignOnPage })),
);

/** Screens built from an approved design. Everything else gets the placeholder. */
const BUILT_ROUTES = new Set([
  '/dashboard',
  '/dashboard/ceo',
  '/agent',
  '/notifications',
  '/compliance',
  '/site-management/weekly',
  '/site-diaries',
  '/business-directory',
  '/defects',
  '/people',
  '/people/dashboard',
  '/people/staff',
  '/people/workers',
  '/people/scorecards',
  '/people/skills-matrix',
  '/people/onboarding',
  '/site-management',
  '/site-management/register',
  '/site-management/warnings',
  '/site-management/timesheets',
  '/site-management/quick-hours',
  '/projects',
  '/projects/register',
  '/projects/new',
  '/company/lessons',
  '/company/triggers',
  '/company/archive',
  '/projects/programme',
  '/warehouse',
  '/finance',
  '/finance/budgets',
  '/finance/purchase-orders',
  '/finance/expenses',
  '/finance/claims',
  '/finance/inventory',
  '/finance/cost-codes',
  '/warehouse/tools',
  '/warehouse/inventory',
  '/warehouse/resources',
  '/prospects',
  '/prospects/follow-ups',
  '/prospects/interactions',
  '/pre-construction',
  '/pre-construction/tenders',
  '/development',
  '/safety',
  '/company',
  '/company/roles',
  '/company/permissions',
  '/company/about',
  '/company/org-chart',
  '/company/kpi-scorecards',
  '/company/sops',
  '/company/templates',
  '/company/data-import',
  '/insights',
]);

function FullPageSpinner() {
  return <ConstructionLoader fullScreen />;
}

/** Blocks the shell until a session is confirmed, remembering the intended route. */
function RequireAuth({ children, allowWorker = false }: { children: ReactNode; allowWorker?: boolean }) {
  const { user, initialising } = useAuth();
  const location = useLocation();

  if (initialising) return <FullPageSpinner />;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  // A worker's login reaches their own site record and nothing else — the API
  // enforces it (ConfineWorkerAccounts); this just avoids a shell full of 404s.
  if (user.role === 'worker' && !allowWorker) return <Navigate to="/me" replace />;
  return <>{children}</>;
}

/**
 * Guards the Delivery area.
 *
 * The API already 404s for anyone outside the client/developer conversation, so
 * this is about the URL rather than the data: someone who types /development
 * should land on the dashboard, not on a broken screen that tells them the area
 * is there.
 */
function RequireFeedbackAccess({ children }: { children: ReactNode }) {
  const { user, initialising } = useAuth();
  if (initialising) return <FullPageSpinner />;
  if (!user?.feedback_access) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

/**
 * Guards the executive view.
 *
 * Same reasoning as RequireFeedbackAccess: the API 404s for anyone who may not
 * read the company's figures, so this exists to send them somewhere sensible
 * rather than to a screen that has failed to load.
 */
function RequireExecutiveAccess({ children }: { children: ReactNode }) {
  const { user, initialising } = useAuth();
  if (initialising) return <FullPageSpinner />;
  if (!user?.executive_access) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

/**
 * Finance — its own audience since round 7.
 *
 * A Project Admin runs a job's financials without reading the company's
 * pipeline and cash position, so this asks the finance question rather than the
 * executive one. The API answers 404 to anyone else either way.
 */
function RequireFinanceAccess({ children }: { children: ReactNode }) {
  const { user, initialising } = useAuth();
  if (initialising) return <FullPageSpinner />;
  if (!user?.finance_access) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

/** Keeps a signed-in user out of the auth screens. */
function RedirectIfAuthenticated({ children }: { children: ReactNode }) {
  const { user, initialising } = useAuth();
  if (initialising) return <FullPageSpinner />;
  if (user) return <Navigate to={user.role === 'worker' ? '/me' : homePathFor({ executive: Boolean(user.executive_access) })} replace />;
  return <>{children}</>;
}

function AppRoutes() {
  const { user } = useAuth();
  // A worker login never renders the shell, and the API would answer 404.
  const projectList = useProjects('', 'active', Boolean(user) && user?.role !== 'worker');

  const projects: ProjectOption[] = (projectList.data?.data ?? []).map((project) => ({
    id: project.id,
    name: project.name,
    color: project.color,
    status: project.status === 'active' ? 'Active Project' : project.status,
    location: project.location_label,
  }));

  return (
    <CurrentProjectProvider projects={projects}>
      <ShellRoutes />
    </CurrentProjectProvider>
  );
}

function ShellRoutes() {
  const { user } = useAuth();
  // One selection shared by every screen — see CurrentProjectContext.
  const { projectId: currentProjectId, setProjectId: setCurrentProjectId, project, projects } = useCurrentProject();
  const currentProject = { name: project?.name ?? 'All projects' };

  // Placeholder routes for every nav destination without an approved design.
  const placeholderPaths = Array.from(
    new Set([...NAV_ITEMS.map((item) => item.path), ...Object.keys(SUB_PAGE_LABELS)]),
  ).filter((path) => !BUILT_ROUTES.has(path));

  return (
    <Suspense fallback={<FullPageSpinner />}>
      <Routes>
        {/* Public: what a scanned site poster opens. No shell, no auth. */}
        <Route path="/s/:token" element={<SiteSignOnPage />} />

        {/* Public: where an invitation email lands. */}
        <Route
          path="/accept-invitation"
          element={
            <RedirectIfAuthenticated>
              <AcceptInvitationPage />
            </RedirectIfAuthenticated>
          }
        />

        {/* A worker's own site record. No admin shell. */}
        <Route
          path="/me"
          element={
            <RequireAuth allowWorker>
              <MySiteRecordPage />
            </RequireAuth>
          }
        />

        <Route
          path="/login"
          element={
            <RedirectIfAuthenticated>
              <SignInPage />
            </RedirectIfAuthenticated>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <RedirectIfAuthenticated>
              <ForgotPasswordPage />
            </RedirectIfAuthenticated>
          }
        />
        <Route
          path="/reset-password"
          element={
            <RedirectIfAuthenticated>
              <ResetPasswordPage />
            </RedirectIfAuthenticated>
          }
        />

        <Route
          element={
            <RequireAuth>
              <AppShell
                projects={projects}
                currentProjectId={currentProjectId}
                onProjectChange={setCurrentProjectId}
              />
            </RequireAuth>
          }
        >
          <Route index element={<Navigate to={homePathFor({ executive: Boolean(user?.executive_access) })} replace />} />
          {/*
            One dashboard per role (client round 6). An executive's home is the
            CEO Dashboard, so /dashboard sends them there rather than showing a
            second, near-identical screen.
          */}
          <Route
            path="/dashboard"
            element={
              user?.executive_access ? <Navigate to="/dashboard/ceo" replace /> : <DashboardsPage />
            }
          />
          <Route
            path="/dashboard/ceo"
            element={
              <RequireExecutiveAccess>
                <DashboardsPage />
              </RequireExecutiveAccess>
            }
          />

          {/* The old dashboards list; everybody now lands on their own one. */}
          <Route path="/dashboards" element={<Navigate to={homePathFor({ executive: Boolean(user?.executive_access) })} replace />} />

          {/* OneParacon Agent — conversations are private to the person; tools follow their role. */}
          <Route path="/agent" element={<AgentPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/site-management/weekly" element={<WeeklyAttendancePage />} />
          <Route path="/site-diaries" element={<SiteDiariesPage />} />
          <Route path="/defects" element={<DefectsPage />} />
          <Route path="/defects/:id" element={<DefectDetailPage />} />
          <Route path="/compliance" element={<CompliancePage />} />
          <Route path="/compliance/documents/:id" element={<ComplianceDocumentPage />} />
          <Route path="/agent/:id" element={<AgentPage />} />

          {/* Site Management owns Live Attendance, the Register, Warnings and Timesheets. */}
          <Route
            path="/site-management"
            element={<SiteManagementPage projectId={currentProjectId} projectName={currentProject.name} />}
          />
          <Route
            path="/site-management/register"
            element={<SiteManagementPage projectId={currentProjectId} projectName={currentProject.name} />}
          />
          <Route
            path="/site-management/warnings"
            element={<SiteManagementPage projectId={currentProjectId} projectName={currentProject.name} />}
          />
          <Route
            path="/site-management/timesheets"
            element={<SiteManagementPage projectId={currentProjectId} projectName={currentProject.name} />}
          />
          <Route path="/site-management/quick-hours" element={<QuickHoursPage />} />
          {/* Older links into the register. */}
          {/* Companies — clients, suppliers and subcontractors — a tab of People. */}
          <Route path="/business-directory" element={<DirectoryPage />} />
          <Route path="/business-directory/new" element={<DirectoryFormPage />} />
          <Route path="/business-directory/:id" element={<DirectoryDetailPage />} />
          <Route path="/business-directory/:id/edit" element={<DirectoryFormPage />} />
          <Route path="/sign-ons" element={<Navigate to="/site-management/register" replace />} />
          <Route path="/site-management/sign-ons" element={<Navigate to="/site-management/register" replace />} />
          <Route path="/home" element={<Navigate to="/dashboard" replace />} />

          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/projects/register" element={<ProjectsPage view="register" />} />
          <Route path="/projects/new" element={<ProjectFormPage />} />
          {/*
            Router v6 ranks by specificity rather than declaration order, so the
            static `lessons` segment wins over `:id` wherever it sits here.
          */}
          <Route path="/company/lessons" element={<LessonsPage />} />
          <Route path="/company/triggers" element={<TriggersPage />} />
          <Route path="/company/archive" element={<ArchivePage />} />
          {/* The register moved to Company; a bookmark must not 404. */}
          <Route path="/projects/lessons" element={<Navigate to="/company/lessons" replace />} />
          {/* The programme (Gantt): every project stacked, and one project's editor. */}
          <Route path="/projects/programme" element={<PortfolioProgrammePage />} />
          <Route path="/projects/:id/programme" element={<ProgrammePage />} />
          <Route path="/projects/:id" element={<ProjectDetailPage />} />
          <Route path="/projects/:id/edit" element={<ProjectFormPage />} />
          {/* Round 3 item 6. The API gates it on cost visibility. */}
          <Route path="/projects/:id/wip" element={<WipSchedulePage />} />

          <Route path="/warehouse" element={<WarehousePage />} />
          <Route path="/warehouse/tools" element={<WarehousePage />} />
          <Route path="/warehouse/tools/new" element={<ToolFormPage />} />
          <Route path="/warehouse/tools/:id/edit" element={<ToolFormPage />} />
          <Route path="/warehouse/inventory" element={<WarehousePage />} />
          <Route path="/warehouse/inventory/new" element={<InventoryFormPage />} />
          <Route path="/warehouse/inventory/:id/edit" element={<InventoryFormPage />} />
          <Route path="/warehouse/resources" element={<WarehousePage />} />

          <Route path="/prospects" element={<ProspectsPage />} />
          <Route path="/prospects/new" element={<ProspectFormPage />} />
          <Route path="/prospects/:id/edit" element={<ProspectFormPage />} />
          <Route path="/prospects/follow-ups" element={<ProspectsPage />} />
          <Route path="/prospects/interactions" element={<ProspectsPage />} />

          <Route path="/pre-construction" element={<PreConstructionPage />} />
          <Route path="/pre-construction/tenders" element={<PreConstructionPage />} />
          <Route path="/pre-construction/tenders/new" element={<TenderFormPage />} />
          <Route path="/pre-construction/tenders/:id" element={<TenderProfilePage />} />
          <Route path="/pre-construction/tenders/:id/edit" element={<TenderFormPage />} />

          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/profile/security" element={<ProfilePage />} />
          <Route path="/profile/notifications" element={<ProfilePage />} />


          <Route path="/company" element={<CompanyPage />} />
          <Route path="/company/users" element={<CompanyPage />} />
          <Route path="/company/permissions" element={<CompanyPage />} />
          {/* Items 3-5 of the round-3 list, keyed on the role that joins them. */}
          <Route path="/company/roles" element={<RolesPage />} />
          {/* Round 3 items 1 and 11 — the statement, and the SOP register. */}
          <Route path="/company/about" element={<CompanyStatementPage />} />
          <Route path="/company/sops" element={<SopsPage />} />
          <Route path="/company/templates" element={<TemplatesPage />} />
          {/*
            Before the document route, so a checklist is not read as a
            document's id. Static segments win over a parameter either way;
            declared first so the order is not a thing to know.
          */}
          <Route path="/company/templates/checklists/:kind" element={<ChecklistMasterPage />} />
          <Route path="/company/templates/:templateId" element={<DocumentPage />} />
          {/*
            The same document, read for one job: here the short codes are
            filled with that project's details. In the company library they
            stay as codes, because a master template belongs to no job.
          */}
          <Route path="/projects/:projectId/documents/:templateId" element={<ProjectDocumentPage />} />
          {/* The Company screens from the 13 Sep designs. */}
          <Route path="/company/org-chart" element={<OrgChartPage />} />
          <Route path="/company/kpi-scorecards" element={<KpiScorecardsPage />} />
          {/*
            Role checklists live in the module they belong to — "Your Checklist"
            on each module's dashboard, and the weekly email. The Company tab
            was the page nobody opened (round 7).
          */}
          <Route path="/company/checklists" element={<Navigate to="/company/roles" replace />} />
          {/* The checklists moved inside the record they run against (round 6). */}
          <Route path="/pre-construction/checklist" element={<Navigate to="/pre-construction/tenders" replace />} />
          <Route path="/projects/checklist" element={<Navigate to="/projects/register" replace />} />
          <Route
            path="/company/data-import"
            element={
              <RequireExecutiveAccess>
                <DataImportPage />
              </RequireExecutiveAccess>
            }
          />

          {/* Finance — executives and Project Admins; the API answers 404 to anyone else. */}
          <Route path="/finance" element={<RequireFinanceAccess><FinancePage /></RequireFinanceAccess>} />
          <Route path="/finance/budgets" element={<RequireFinanceAccess><FinancePage /></RequireFinanceAccess>} />
          <Route path="/finance/budgets/new" element={<RequireFinanceAccess><BudgetPage /></RequireFinanceAccess>} />
          <Route path="/finance/budgets/:id" element={<RequireFinanceAccess><BudgetPage /></RequireFinanceAccess>} />
          <Route path="/finance/purchase-orders" element={<RequireFinanceAccess><FinancePage /></RequireFinanceAccess>} />
          <Route path="/finance/purchase-orders/new" element={<RequireFinanceAccess><PurchaseOrderFormPage /></RequireFinanceAccess>} />
          <Route path="/finance/purchase-orders/:id" element={<RequireFinanceAccess><PurchaseOrderPage /></RequireFinanceAccess>} />
          <Route path="/finance/purchase-orders/:id/edit" element={<RequireFinanceAccess><PurchaseOrderFormPage /></RequireFinanceAccess>} />
          <Route path="/finance/expenses" element={<RequireFinanceAccess><FinancePage /></RequireFinanceAccess>} />
          <Route path="/finance/claims" element={<RequireFinanceAccess><FinancePage /></RequireFinanceAccess>} />
          <Route path="/finance/inventory" element={<RequireFinanceAccess><FinancePage /></RequireFinanceAccess>} />
          <Route path="/finance/cost-codes" element={<RequireFinanceAccess><FinancePage /></RequireFinanceAccess>} />
          <Route path="/finance/projects/:id" element={<RequireFinanceAccess><ProjectFinancePage /></RequireFinanceAccess>} />

          <Route path="/insights" element={<InsightsPage />} />

          <Route path="/safety" element={<SafetyPage />} />
          <Route path="/safety/certifications" element={<SafetyPage />} />
          <Route path="/safety/inductions" element={<SafetyPage />} />
          <Route path="/safety/requirements" element={<SafetyPage />} />
          <Route path="/safety/documents" element={<SafetyPage />} />
          <Route path="/safety/types" element={<SafetyPage />} />
          <Route path="/site-management/roll-call/:id" element={<RollCallPage />} />
          <Route path="/safety/inductions/new" element={<InductionCourseFormPage />} />
          <Route path="/safety/inductions/:id/edit" element={<InductionCourseFormPage />} />
          <Route path="/safety/inductions/:id/build" element={<InductionBuilderPage />} />
          <Route path="/safety/types/new" element={<CertificationTypeFormPage />} />
          <Route path="/safety/types/:id/edit" element={<CertificationTypeFormPage />} />

          {/* Delivery — the client/developer review area, gated by RequireFeedbackAccess. */}
          <Route
            path="/development"
            element={
              <RequireFeedbackAccess>
                <DevelopmentPage />
              </RequireFeedbackAccess>
            }
          />
          <Route
            path="/development/board"
            element={
              <RequireFeedbackAccess>
                <DevelopmentPage />
              </RequireFeedbackAccess>
            }
          />
          <Route
            path="/development/comments"
            element={
              <RequireFeedbackAccess>
                <DevelopmentPage />
              </RequireFeedbackAccess>
            }
          />
          <Route
            path="/development/tickets/new"
            element={
              <RequireFeedbackAccess>
                <TicketFormPage />
              </RequireFeedbackAccess>
            }
          />
          <Route
            path="/development/tickets/:id"
            element={
              <RequireFeedbackAccess>
                <TicketDetailPage />
              </RequireFeedbackAccess>
            }
          />
          <Route
            path="/development/tickets/:id/edit"
            element={
              <RequireFeedbackAccess>
                <TicketFormPage />
              </RequireFeedbackAccess>
            }
          />

          <Route path="/people" element={<PeoplePage />} />
          <Route path="/people/dashboard" element={<PeopleDashboardPage />} />
          <Route path="/people/new" element={<PersonFormPage />} />
          <Route path="/people/scorecards" element={<ScorecardsPage />} />
          <Route path="/people/scorecards/new" element={<ScorecardFormPage />} />
          <Route path="/people/scorecards/:id/edit" element={<ScorecardFormPage />} />
          <Route path="/people/skills-matrix" element={<SkillsMatrixPage />} />
          <Route path="/people/skills-matrix/catalogue" element={<SkillCataloguePage />} />
          <Route path="/people/onboarding" element={<OnboardingPage />} />
          {/* Staff and Workers are one screen with a view toggle now. */}
          <Route path="/people/staff" element={<Navigate to="/people?view=staff" replace />} />
          <Route path="/people/staff/:id" element={<PersonDetailPage />} />
          <Route path="/people/staff/:id/edit" element={<PersonFormPage />} />
          <Route path="/people/workers" element={<Navigate to="/people?view=workers" replace />} />
          <Route path="/people/workers/:id" element={<PersonDetailPage />} />
          <Route path="/people/workers/:id/edit" element={<PersonFormPage />} />
          <Route path="/people/:id" element={<PersonDetailPage />} />
          <Route path="/people/:id/edit" element={<PersonFormPage />} />

          {placeholderPaths.map((path) => (
            <Route key={path} path={path} element={<PlaceholderPage />} />
          ))}

          <Route path="*" element={<PlaceholderPage />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

export function App() {
  return (
    <ToastProvider>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </ToastProvider>
  );
}
